"use client";

import { useCallback, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { FolderOpen, Plus, Upload } from "lucide-react";
import { type Accept, useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@repo/ui";
import { useMediaUpload, type MediaUpload } from "@/hooks/use-media-upload";
import { useRelationEntityList } from "@/hooks/use-relation-entity-list";
import type {
  ItemsToConnect,
  ItemsToDisconnect,
  ItemsToSet,
} from "@/services/entity-instance.service";
import type { PyloMedia } from "@/services/pylo-media.service";
import { MediaFieldTile } from "./media-library/media-field-tile";
import { MediaPickerDialog } from "./media-library/media-picker-dialog";
import { describeAcceptedFiles } from "./media-library/media-utils";

interface DropzoneProps {
  fieldName: string;
  isList: boolean;
  itemsToConnect: ItemsToConnect;
  itemsToDisconnect: ItemsToDisconnect;
  itemsToSet: ItemsToSet;
  setItemsToConnect: Dispatch<SetStateAction<ItemsToConnect>>;
  setItemsToDisconnect: Dispatch<SetStateAction<ItemsToDisconnect>>;
  setItemsToSet: Dispatch<SetStateAction<ItemsToSet>>;
  parentEntityName: string;
  parentEntityId?: string;
  accept?: Accept;
  /** The relation's `media_allowed_computed_mime_types`; scopes the file picker. */
  allowedMimeTypes?: Array<string>;
}

/** One tile in the field, whether it is already connected or still uploading. */
interface FieldFile {
  key: string;
  mediaId?: string;
  uploadKey?: string;
  name?: string;
  url?: string;
  mimeType?: string;
  sizeBytes?: number;
  status?: MediaUpload["status"];
  progress?: number;
  error?: string | null;
  uploadedBytes?: number;
  uploadSpeed?: number;
  eta?: number;
}

export const Dropzone = ({
  fieldName,
  isList,
  setItemsToConnect,
  setItemsToDisconnect,
  setItemsToSet,
  parentEntityName,
  parentEntityId,
  accept,
  allowedMimeTypes,
}: DropzoneProps) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState<Array<PyloMedia>>([]);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const gridRef = useRef<HTMLDivElement>(null);

  const connectedQuery = useRelationEntityList({
    fieldName,
    id: parentEntityId,
    entityName: parentEntityName,
    // The url is what every tile renders from, so it is always fetched.
    fieldsToFetchForRelationFieldNames: [
      {
        fieldName,
        fieldsToFetch: ["url", "file_name", "id", "file_mime_type", "file_size_bytes"],
      },
    ],
  });

  const connect = useCallback(
    (mediaId: string) => {
      if (isList) {
        setItemsToConnect((previous) => ({
          ...previous,
          [fieldName]: [...(previous[fieldName] ?? []), { id: mediaId }],
        }));
      } else {
        setItemsToSet((previous) => ({ ...previous, [fieldName]: { id: mediaId } }));
      }
    },
    [fieldName, isList, setItemsToConnect, setItemsToSet],
  );

  const { uploads, start, remove: removeUpload } = useMediaUpload({ onCreated: connect });

  const onDrop = useCallback(
    (files: Array<File>) => {
      if (files.length === 0) return;
      // A single-value relation holds one file, so a new upload supersedes the old.
      start(isList ? files : files.slice(0, 1), { replace: !isList });
    },
    [isList, start],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    onDropRejected: (rejections) =>
      toast.error(
        rejections.length === 1
          ? `${rejections[0].file.name} is not an accepted file type.`
          : `${rejections.length} files are not an accepted file type.`,
      ),
    accept,
    multiple: isList,
    // The container is a drop target only — adding is an explicit affordance, so a
    // stray click on a tile never opens the OS file dialog.
    noClick: true,
    noKeyboard: true,
  });

  const connected = useMemo<Array<PyloMedia>>(() => {
    const pages = connectedQuery.data?.pages.flatMap((page) => page?.data) ?? [];
    return pages.filter(Boolean) as Array<PyloMedia>;
  }, [connectedQuery.data]);

  const files = useMemo<Array<FieldFile>>(() => {
    const seen = new Set<string>();
    const tiles: Array<FieldFile> = [];

    for (const media of [...connected, ...picked]) {
      if (!media?.id || seen.has(media.id) || removedIds.has(media.id)) continue;
      seen.add(media.id);
      tiles.push({
        key: media.id,
        mediaId: media.id,
        name: media.file_name,
        url: media.url,
        mimeType: media.file_mime_type,
        sizeBytes: media.file_size_bytes,
      });
    }

    for (const upload of uploads) {
      // Once a refetch brings the finished upload back as a connected file, the
      // server row wins — it carries a durable url instead of an object URL.
      if (upload.mediaId && seen.has(upload.mediaId)) continue;
      if (upload.mediaId && removedIds.has(upload.mediaId)) continue;
      tiles.push({
        key: upload.key,
        uploadKey: upload.key,
        mediaId: upload.mediaId,
        name: upload.file.name,
        url: upload.preview,
        mimeType: upload.file.type,
        sizeBytes: upload.file.size,
        status: upload.status,
        progress: upload.progress,
        error: upload.error,
        uploadedBytes: upload.uploadedBytes,
        uploadSpeed: upload.uploadSpeed,
        eta: upload.eta,
      });
    }

    return isList ? tiles : tiles.slice(-1);
  }, [connected, picked, uploads, removedIds, isList]);

  const removeFile = (file: FieldFile) => {
    if (file.uploadKey) removeUpload(file.uploadKey);

    const mediaId = file.mediaId;
    if (!mediaId) return;

    setRemovedIds((previous) => new Set(previous).add(mediaId));
    setPicked((previous) => previous.filter((media) => media.id !== mediaId));

    if (!isList) {
      setItemsToSet((previous) => ({ ...previous, [fieldName]: null }));
      return;
    }

    setItemsToConnect((previous) => ({
      ...previous,
      [fieldName]: (previous[fieldName] ?? []).filter((item) => item.id !== mediaId),
    }));

    // Only a file the server already holds needs disconnecting; one added in this
    // session is undone by dropping it from itemsToConnect.
    if (connected.some((media) => media.id === mediaId)) {
      setItemsToDisconnect((previous) => ({
        ...previous,
        [fieldName]: [...(previous[fieldName] ?? []), { id: mediaId }],
      }));
    }
  };

  const addExisting = (selected: Array<PyloMedia>) => {
    const present = new Set(files.map((file) => file.mediaId).filter(Boolean));
    const additions = selected.filter((media) => !present.has(media.id));
    if (additions.length === 0) return;

    const restored = new Set(additions.map((media) => media.id));
    setRemovedIds((previous) => new Set([...previous].filter((id) => !restored.has(id))));

    if (isList) {
      // Re-picking a file removed earlier in this session must cancel its disconnect,
      // or the save would connect and disconnect the same row.
      setItemsToDisconnect((previous) => ({
        ...previous,
        [fieldName]: (previous[fieldName] ?? []).filter((item) => !restored.has(item.id as string)),
      }));
      setPicked((previous) => [...previous, ...additions]);
      additions.forEach((media) => connect(media.id));
    } else {
      setPicked([additions[0]]);
      connect(additions[0].id);
    }
  };

  const onScroll = () => {
    const container = gridRef.current;
    if (!container) return;
    const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 50;
    if (nearBottom && connectedQuery.hasNextPage && !connectedQuery.isFetchingNextPage) {
      connectedQuery.fetchNextPage();
    }
  };

  const isEmpty = files.length === 0;
  const addLabel = isList ? "Add" : "Replace";

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[12px] text-muted-foreground">
        {describeAcceptedFiles(allowedMimeTypes)}
        {" · "}
        {isList ? `${files.length} file${files.length === 1 ? "" : "s"}` : "single file"}
      </p>

      <div
        {...getRootProps()}
        className={cn(
          "relative rounded-lg transition-colors",
          // Empty, the add area is the only thing in the box, so the box itself
          // would just be a second border around it.
          !isEmpty && "border bg-white p-2",
          isDragActive && !isEmpty && "border-primary",
        )}
      >
        <input {...getInputProps()} />

        <div
          ref={gridRef}
          onScroll={onScroll}
          className="grid max-h-64 grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-2 overflow-y-auto"
        >
          {files.map((file) => (
            <MediaFieldTile
              key={file.key}
              name={file.name}
              previewUrl={file.url}
              mimeType={file.mimeType}
              mediaId={file.mediaId}
              sizeBytes={file.sizeBytes}
              status={file.status}
              progress={file.progress}
              error={file.error}
              uploadedBytes={file.uploadedBytes}
              uploadSpeed={file.uploadSpeed}
              eta={file.eta}
              onRemove={() => removeFile(file)}
            />
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex flex-col items-center justify-center gap-1 rounded-md border border-dashed text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-foreground",
                  isEmpty ? "col-span-full h-20" : "aspect-square",
                )}
              >
                <Plus className="h-4 w-4" />
                <span className="px-1 text-center text-[11px] leading-tight">
                  {isEmpty ? `Drop ${isList ? "files" : "a file"} here, or click to add` : addLabel}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem className="text-[13px]" onSelect={() => open()}>
                <Upload className="mr-2 h-3.5 w-3.5" />
                Upload from device
              </DropdownMenuItem>
              <DropdownMenuItem className="text-[13px]" onSelect={() => setPickerOpen(true)}>
                <FolderOpen className="mr-2 h-3.5 w-3.5" />
                Choose from library
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {connectedQuery.isFetchingNextPage && (
            <div className="col-span-full flex justify-center py-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            </div>
          )}
        </div>

        {isDragActive && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/10 text-[13px] font-medium text-primary">
            Drop to upload
          </div>
        )}
      </div>

      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        multiple={isList}
        accept={accept}
        allowedMimeTypes={allowedMimeTypes}
        title={isList ? "Add files" : "Add a file"}
        onConfirm={addExisting}
      />
    </div>
  );
};
